import * as React from 'react';
import { SetStateAction, useEffect, useState } from 'react';
import * as TFF from '@tff/types/TFF';
import { ConfirmRequest, FlightAlternatives, Campaign, TicketIssueRequest, ValidationRequest } from '@tff/types/TFF';
import SearchAlternativeFlights from './SearchAlternativeFlights';
import AlternativeFlightsPage from './AlternativeFlightsPage';
import { useAmendBooking } from '../../../hooks/use-amend-booking';
import ConfirmationPage from './ConfirmationPage';
import TFFDialog from '../../../styled-components/TFFDialog';
import TFFTypography from '../../../styled-components/TFFTypography';
import TFFAlert from '../../../styled-components/TFFAlert';
import { useIntl } from 'react-intl';
import BackButton from '../BackButton';
import { fetchServiceFeeFromCampaign } from '../../../util/fetchServiceFeeFromCampaign';
import { RecordLocator } from '@tff/types';
import { useBookingContext } from '../../../hooks/use-booking-context';

export type SelectedAmendBookings = {
  combiHashCodes: string[];
  journeyONDs: string[];
  offerPrices: string[];
};

interface props {
  orderDetails: TFF.OrderDetails;
  orderSummary: TFF.OrderSummary;
  isRoundTrip: boolean;
  airports: TFF.Airport[];
  recordLocator: string;
  selectedAmendBookings: SelectedAmendBookings;
  setSelectedAmendBookings: (value: SetStateAction<SelectedAmendBookings>) => void;
  tfmPnr: string | undefined;
  rebookCloser: (fn: SetStateAction<boolean>) => void;
  flightDetails: TFF.FlightDetails;
  midocoOrderNo?: number;
  cancelHandler: () => void;
  retrieveDb?: TFF.Retrieve;
  bookingSource: TFF.TffBookingSource;
  campaigns: Campaign[];
}

type rebookStep = 'Search' | 'Alternatives' | 'Validate';
const DEFAULT_NDC_REBOOK_SERVICE_FEE = 30;

const NdcRebook: React.FC<props> = ({
  orderDetails,
  orderSummary,
  isRoundTrip,
  airports,
  recordLocator,
  selectedAmendBookings,
  setSelectedAmendBookings,
  tfmPnr,
  rebookCloser,
  flightDetails,
  midocoOrderNo,
  cancelHandler,
  retrieveDb,
  bookingSource,
  campaigns,
}) => {
  const ndcRebookSteps: rebookStep[] = ['Search', 'Alternatives', 'Validate'];
  const [rebookStep, setRebookStep] = useState<rebookStep>('Search');
  const { setAmendBookingRequest, amendResponse, amendStatus, amendError } = useAmendBooking();
  const [selectedOfferJourneys, setSelectedOfferJourneys] = useState<TFF.Journey[]>([]);
  const [showError, setShowError] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [isOnConfirmation, setIsOnConfirmation] = useState<boolean>(false);
  const [isOnTicketing, setIsOnTicketing] = useState<boolean>(false);
  const handleFinishSearch = () => {
    setRebookStep('Alternatives');
  };
  const [selectedOffer, setSelectedOffer] = React.useState<TFF.CombinationOffer | undefined>();
  const [showConfirmationError, setShowConfirmationError] = React.useState<boolean>(false);
  const [alternativesRequestQuery, setAlternativesRequestQuery] = React.useState<TFF.AmendBookingRequest>();
  const [currentAlternativeFlights, setCurrentAlternativeFlights] = React.useState<FlightAlternatives>();
  const [serviceFee, setServiceFee] = useState<string>('0,00');
  const { numberFormatter } = useBookingContext();

  useEffect(() => {
    const passengerLength: number = Object.values(orderDetails.Passengers).filter(
      passenger => passenger.Type !== 'INF',
    ).length;

    const serviceFeeFromCampaign = fetchServiceFeeFromCampaign(
      'CHANGE',
      orderDetails,
      orderSummary,
      campaigns,
      bookingSource,
    );

    let feeValue = DEFAULT_NDC_REBOOK_SERVICE_FEE * passengerLength;
    if (serviceFeeFromCampaign) {
      feeValue = parseFloat(serviceFeeFromCampaign.fee.toString()) * passengerLength;
    }
    const formattedFeeString = numberFormatter.format(feeValue);
    setServiceFee(formattedFeeString);
  }, []);

  const intl = useIntl();

  const noTicketingNeeded = () => {
    return retrieveDb?.product === 'atcomres' && retrieveDb?.status === 'BOOKED' && !retrieveDb?.ticketed;
  };

  useEffect(() => {
    if (amendStatus === 'SUCCESS' && amendResponse?.query.action === 'search-alternatives') {
      setAlternativesRequestQuery(amendResponse?.query);
      setCurrentAlternativeFlights(amendResponse?.flightAlternatives);
      setRebookStep('Alternatives');
    } else if (amendStatus === 'SUCCESS' && rebookStep === 'Validate' && isOnTicketing) {
      setShowValidation(true);
    } else if (amendStatus === 'FAILURE' && rebookStep === 'Validate' && isOnTicketing) {
      setShowConfirmationError(true);
    } else if (amendStatus === 'SUCCESS' && rebookStep === 'Validate' && isOnConfirmation) {
      setIsOnConfirmation(false);
      if (noTicketingNeeded()) {
        setShowValidation(true);
      } else {
        handleTicketIssue();
      }
    } else if (amendStatus === 'FAILURE' && rebookStep === 'Validate' && isOnConfirmation) {
      setShowConfirmationError(true);
    } else if (amendStatus === 'FAILURE' && rebookStep === 'Validate') {
      setRebookStep('Search');
      setShowError(true);
    }
  }, [amendStatus]);

  const handleBackButton = () => {
    const currentStepIndex = ndcRebookSteps.indexOf(rebookStep);
    if (currentStepIndex !== -1) {
      if (rebookStep === 'Search') {
        rebookCloser(false);
      } else {
        const previousRebookStep = ndcRebookSteps[currentStepIndex - 1];
        setRebookStep(previousRebookStep);
      }
    }
  };

  const handleOfferSelection = async (offer: TFF.CombinationOffer) => {
    const alternativeOfferRequest: ValidationRequest = {
      action: 'validate',
      recordLocator: recordLocator as RecordLocator,
      tfmPnr: amendResponse.query.tfmPnr,
      bookingSource: amendResponse.query.bookingSource,
      offerId: offer.Id,
    };
    setAmendBookingRequest(alternativeOfferRequest);
    setRebookStep('Validate');
  };

  const handleOfferConfirmation = async () => {
    const confirmRequest: ConfirmRequest = {
      action: 'confirm',
      recordLocator: recordLocator as RecordLocator,
      tfmPnr: amendResponse.query.tfmPnr,
      bookingSource: amendResponse.query.bookingSource,
      offerId: amendResponse?.ndcValidationResponse?.offerId,
      combinations: [
        {
          AdditionalParams: {
            ...amendResponse?.ndcValidationResponse?.paxOfferItemIds,
            OfferId: amendResponse?.ndcValidationResponse?.offerId,
            OfferItemIds: amendResponse?.ndcValidationResponse?.offerItemIds.join(','),
          },
        },
      ],
    };
    setAmendBookingRequest(confirmRequest);
    setIsOnConfirmation(true);
  };

  const handleTicketIssue = () => {
    const ticketIssueRequest: TicketIssueRequest = {
      action: 'ticket-issue',
      recordLocator: recordLocator as RecordLocator,
      tfmPnr: amendResponse?.query?.tfmPnr!,
      bookingSource: amendResponse?.query?.bookingSource!,
      midocoOrderNo: midocoOrderNo,
      serviceFee: parseInt(serviceFee),
      ndcConfirmResponse: amendResponse?.ndcConfirmResponse,
      offerId: amendResponse?.offerId,
    };
    setAmendBookingRequest(ticketIssueRequest);
    setIsOnTicketing(true);
  };

  const handleShowScreenByStep = () => {
    switch (rebookStep) {
      case 'Search':
        return (
          <>
            {showError && (
              <TFFDialog
                dialogId="validationError"
                header={<TFFTypography text="Error" />}
                isOpen={showError}
                children={<div>Connection Error. Please try again</div>}
                closeButton
                onCancel={() => setShowError(false)}
              />
            )}
            <SearchAlternativeFlights
              orderDetails={orderDetails}
              isRoundTrip={isRoundTrip}
              journeys={orderDetails.Journeys}
              airports={airports}
              recordLocator={recordLocator}
              selectedAmendBookings={selectedAmendBookings}
              setSelectedAmendBookings={setSelectedAmendBookings}
              tfmPnr={tfmPnr}
              rebookCloser={rebookCloser}
              flightDetails={flightDetails}
              midocoOrderNo={midocoOrderNo}
              cancelHandler={cancelHandler}
              retrieveDb={retrieveDb}
              searchFinished={handleFinishSearch}
              setAmendBookingRequest={setAmendBookingRequest}
              amendResponse={amendResponse}
              amendStatus={amendStatus}
              amendError={amendError}
            />
          </>
        );
      case 'Alternatives':
        return (
          <AlternativeFlightsPage
            journeys={orderDetails.Journeys}
            isRoundTrip={isRoundTrip}
            setRebookStep={setRebookStep}
            rebookCloser={rebookCloser}
            flightAlternatives={currentAlternativeFlights}
            handleOfferSelection={handleOfferSelection}
            setSelectedOfferJourneys={setSelectedOfferJourneys}
            setGetOffer={setSelectedOffer}
          />
        );
      case 'Validate':
        return (
          <>
            {showConfirmationError && (
              <TFFDialog
                dialogId="validationError"
                header={
                  <TFFTypography
                    text="Amend notification"
                    fontWeight="bold"
                    extraStyle={{
                      fontWeight: 700,
                      fontSize: '24px',
                      lineHeight: '27px',
                      font: 'Ambit',
                      color: '#1A115A',
                    }}
                  />
                }
                isOpen={showConfirmationError}
                children={
                  <TFFAlert
                    variant="error"
                    text={intl.formatMessage({ id: isOnTicketing ? 'rebook.ticketingError' : 'rebook.rebookError' })}
                  />
                }
                cancelButton
                confirmButton={!isOnTicketing}
                onCancel={() => rebookCloser(false)}
                onConfirm={() => {
                  setShowConfirmationError(false);
                  setAmendBookingRequest(alternativesRequestQuery!);
                }}
                confirmButtonText={intl.formatMessage({ id: 'rebook.retry' })}
              />
            )}
            {showValidation && (
              <TFFDialog
                dialogId="validation"
                header={
                  <TFFTypography
                    text="Amend notification"
                    fontWeight="bold"
                    extraStyle={{
                      fontWeight: 700,
                      fontSize: '24px',
                      lineHeight: '27px',
                      font: 'Ambit',
                      color: '#1A115A',
                    }}
                  />
                }
                isOpen={showValidation}
                children={<TFFAlert variant="success" text={intl.formatMessage({ id: 'ndc.rebookAlert' })} />}
                closeButton
                onCancel={() => {
                  setShowValidation(false);
                  window.location.reload();
                }}
                closeButtonText={intl.formatMessage({ id: 'ndc.rebookReloadPnr' })}
              />
            )}
            <ConfirmationPage
              originalJourneys={orderDetails.Journeys}
              newBookingJourneys={selectedOfferJourneys}
              isRoundTrip={isRoundTrip}
              fareDifference={amendResponse?.ndcValidationResponse?.amendTotalCost ?? 0}
              //@ts-ignore
              taxAmount={amendResponse?.ndcValidationResponse?.amendCostBreakdown.taxSummary}
              //@ts-ignore
              penalty={amendResponse?.ndcValidationResponse?.amendCostBreakdown.fee}
              //@ts-ignore
              baseAmount={amendResponse?.ndcValidationResponse?.amendCostBreakdown.baseAmount}
              rebookCloser={rebookCloser}
              handleConfirmation={handleOfferConfirmation}
              selectedOffer={selectedOffer}
              setServiceFee={setServiceFee}
              serviceFee={serviceFee}
            />
          </>
        );
    }
  };

  return (
    <>
      <BackButton handleClick={handleBackButton} />
      {handleShowScreenByStep()}
    </>
  );
};

export default NdcRebook;
